/*
  Ora Select – pasek wyszukiwania w nagłówku z podpowiedziami produktów.
  Renderowany przez snippets/search.liquid. Korzysta z Shopify Predictive Search API
  (/search/suggest.json) – tego samego co okno wyszukiwania motywu.
*/
(() => {
  if (customElements.get('ora-search-bar')) return;

  const LIMIT = 6;
  const DEBOUNCE = 200;

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatPrice = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    // jak w reszcie sklepu: 1.068,00 zł (pl-PL w przeglądarce nie grupuje liczb 4-cyfrowych)
    const [int, dec] = n.toFixed(2).split('.');
    return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec} zł`;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch {
      return url;
    }
  };

  class OraSearchBar extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-results]');
      this.suggestUrl = this.dataset.suggestUrl;
      this.searchUrl = this.form.getAttribute('action');
      this.active = -1;
      this.controller = null;
      this.timer = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.input.value.trim() && this.panel.innerHTML && this.open());
      this.input.addEventListener('keydown', (e) => this.onKeyDown(e));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.close();
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });
    }

    onInput() {
      clearTimeout(this.timer);
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.close();
        this.panel.innerHTML = '';
        return;
      }
      this.timer = setTimeout(() => this.fetchResults(q), DEBOUNCE);
    }

    async fetchResults(q) {
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product',
        'resources[limit]': LIMIT,
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${this.suggestUrl}.json?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (this.input.value.trim() !== q) return; // użytkownik pisze dalej
        this.render(q, data?.resources?.results?.products || []);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[ora-search-bar]', err);
      }
    }

    render(q, products) {
      this.active = -1;
      const allUrl = `${this.searchUrl}?q=${encodeURIComponent(q)}&options[prefix]=last`;
      if (!products.length) {
        this.panel.innerHTML = `<p class="ora-search-bar__empty">Brak wyników dla „${esc(q)}”.</p>`;
        this.open();
        return;
      }
      this.panel.innerHTML = `
        <ul class="ora-search-bar__list" role="listbox" id="${this.id}-listbox">
          ${products
            .map(
              (p, i) => `
            <li role="option" id="${this.id}-opt-${i}" aria-selected="false">
              <a href="${esc(p.url)}" class="ora-search-bar__item" tabindex="-1">
                <span class="ora-search-bar__thumb">
                  ${p.image ? `<img src="${esc(sizedImage(p.image, 120))}" alt="" width="60" height="60" loading="lazy">` : ''}
                </span>
                <span class="ora-search-bar__text">
                  <span class="ora-search-bar__title">${esc(p.title)}</span>
                  <span class="ora-search-bar__meta">
                    ${p.available === false ? '<span class="ora-search-bar__soldout">Wyprzedane</span>' : ''}
                    <span class="ora-search-bar__price">${esc(formatPrice(p.price))}</span>
                  </span>
                </span>
              </a>
            </li>`
            )
            .join('')}
        </ul>
        <a href="${esc(allUrl)}" class="ora-search-bar__all" tabindex="-1">Pokaż wszystkie wyniki dla „${esc(q)}” →</a>`;
      this.open();
    }

    items() {
      return [...this.panel.querySelectorAll('.ora-search-bar__item, .ora-search-bar__all')];
    }

    onKeyDown(e) {
      const items = this.items();
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (!items.length || this.panel.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + dir + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-active', i === this.active));
        const option = items[this.active].closest('[role="option"]');
        this.panel.querySelectorAll('[role="option"]').forEach((o) => o.setAttribute('aria-selected', String(o === option)));
        this.input.setAttribute('aria-activedescendant', option?.id || '');
        items[this.active].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.active >= 0) {
        e.preventDefault();
        location.href = items[this.active].href;
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }
  }

  customElements.define('ora-search-bar', OraSearchBar);
})();
